/**
 * Card shell for the notes feed — what a detail-page tab renders.
 *
 * The drawer variant used by the tenant profile wraps the same `NotesThread`;
 * only the frame differs, so the two can never drift apart in behaviour.
 */
import { Lock, StickyNote } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { NoteScope } from "@/hooks/v2/useEntityNotes";
import { NotesThread } from "./NotesThread";

export function NotesPanel({
  scope,
  title = "Notes",
  /** Names what belongs in this particular feed, shown while it is empty. */
  emptyHint,
  className,
}: {
  scope: NoteScope;
  title?: string;
  emptyHint?: string;
  className?: string;
}) {
  return (
    <Card className={cn("p-5", className)}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          <StickyNote className="h-4 w-4" />
          {title}
        </h3>
        {/* Operators paste things here they would never put in a tenant-visible
            field. Saying who can read it, on the surface itself, is the whole
            reason they trust it. */}
        <span className="inline-flex items-center gap-1.5 rounded-full bg-sand px-2.5 py-1 text-[11.5px] font-semibold text-body-soft">
          <Lock className="h-3 w-3" />
          Internal — admin only
        </span>
      </div>
      <NotesThread scope={scope} emptyHint={emptyHint} />
    </Card>
  );
}
